import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Play, Square, Volume2 } from 'lucide-react';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';

// Muestras cacheadas en el backend: la primera vez por voz se genera, después
// es instantáneo. No consume créditos del usuario.
const cache = new Map();

export const VoicePreviewButton = ({ voice, testId = 'voice-preview-btn' }) => {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const { authFetchJson } = useAuthenticatedFetch();

  const stop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
  };

  useEffect(() => stop, []);

  useEffect(() => {
    stop();
    setPlaying(false);
    setError(null);
  }, [voice]);

  const play = useCallback(async () => {
    if (playing) {
      stop();
      setPlaying(false);
      return;
    }
    setError(null);
    let url = cache.get(voice);
    if (!url) {
      setLoading(true);
      try {
        const data = await authFetchJson(`/api/voice-previews/elev2/${encodeURIComponent(voice)}`);
        url = data.url;
        cache.set(voice, url);
      } catch {
        setError('No se pudo cargar la muestra');
        setLoading(false);
        return;
      }
      setLoading(false);
    }
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.onended = () => setPlaying(false);
    audio.onerror = () => { setPlaying(false); setError('No se pudo reproducir'); };
    try {
      await audio.play();
      setPlaying(true);
    } catch {
      setError('No se pudo reproducir');
    }
  }, [voice, playing, authFetchJson]);

  return (
    <>
      <button
        type="button"
        className={`voice-preview-btn ${playing ? 'playing' : ''}`}
        onClick={play}
        disabled={loading}
        title={`Escuchar una muestra de ${voice} (no consume créditos)`}
        data-testid={testId}
      >
        {loading ? <Loader2 size={11} className="animate-spin" />
          : playing ? <Square size={11} />
          : <Play size={11} />}
        <Volume2 size={11} />
      </button>
      {error && <span className="voice-preview-error" data-testid={`${testId}-error`}>{error}</span>}
    </>
  );
};

export default VoicePreviewButton;
